import type {
  OrganizationOverviewSloMetric,
  OrganizationOverviewSloMetricValue,
  OrganizationOverviewSloStatus,
  OrganizationOverviewSloUnit,
} from '@cast/types';
import { dashesOrNum, toNumberWithSeparators, toNumOrZero } from '@cast/utils';

import { formatLatency } from './formatLatency';
import type { SloColumnData } from './sloColumnData';
import {
  EMPTY_METRIC_VALUE,
  type SloMetric,
  type SloMetricStatus,
} from './sloHealthMetrics';

type OverviewSloHeatmapData = {
  count: number;
  state: 'ok' | 'risk' | 'breach' | 'gathering';
};

const METRIC_ID_BY_API: Partial<Record<OrganizationOverviewSloMetric, string>> =
  {
    SLO_METRIC_LATENCY_P95: 'latency',
    SLO_METRIC_ERROR_RATE: 'errRate',
    SLO_METRIC_REQUEST_RATE: 'reqRate',
    SLO_METRIC_OOM_KILLS: 'oom',
    SLO_METRIC_RESTARTS: 'restarts',
    SLO_METRIC_NODE_AVAILABILITY: 'nodeAvailability',
    SLO_METRIC_NODE_FAILURES: 'nodeFailures',
    SLO_METRIC_CPU_SATURATION: 'cpuSaturation',
  };

const METRIC_LABEL_BY_ID: Record<string, string> = {
  latency: 'Latency',
  errRate: 'Error rate',
  reqRate: 'Request rate',
  oom: 'OOM kills',
  restarts: 'Restarts',
  disrupt: 'Disruptions',
  nodeAvailability: 'Node availability',
  nodeFailures: 'Node failures',
  cpuSaturation: 'CPU saturation',
};

/** Prefer agent RED metrics, then agentless infra signals. */
const OVERVIEW_SLO_METRIC_ORDER = [
  'latency',
  'errRate',
  'reqRate',
  'oom',
  'restarts',
  'disrupt',
  'nodeAvailability',
  'nodeFailures',
  'cpuSaturation',
] as const;

const mapSloStatus = (
  status?: OrganizationOverviewSloStatus
): SloMetricStatus => {
  switch (status) {
    case 'SLO_STATUS_POOR':
      return 'breached';
    case 'SLO_STATUS_CONCERNING':
      return 'at_risk';
    case 'SLO_STATUS_HEALTHY':
    case 'SLO_STATUS_UNSPECIFIED':
    default:
      return 'ok';
  }
};

const formatRatioPercent = (
  ratio: number
): { value: string; unit: string } => ({
  value: String(dashesOrNum({ value: ratio * 100, roundBy: 1 })),
  unit: '%',
});

const formatRatePerSecond = (
  rate: number
): { value: string; unit: string } => ({
  value: String(
    dashesOrNum({
      value: rate,
      roundBy: rate >= 10 ? 1 : 2,
    })
  ),
  unit: '/s',
});

const formatCount = (count: number): { value: string } => ({
  value: toNumberWithSeparators(Math.round(count)),
});

const formatSecondsLatency = (
  seconds: number
): { value: string; unit?: string } => {
  const formatted = formatLatency(seconds * 1000, 1);
  if (!formatted) {
    return { value: EMPTY_METRIC_VALUE };
  }

  return {
    value: String(formatted.value),
    unit: formatted.unit,
  };
};

const formatOverviewSloValue = (
  value: string | null | undefined,
  unit?: OrganizationOverviewSloUnit
): { value: string; unit?: string } => {
  // Entry present with unset value → render "--" (API contract).
  if (value == null || value === '') {
    return { value: EMPTY_METRIC_VALUE };
  }

  const numeric = toNumOrZero(value);

  switch (unit) {
    case 'SLO_UNIT_RATIO':
      return formatRatioPercent(numeric);
    case 'SLO_UNIT_PER_SECOND':
      return formatRatePerSecond(numeric);
    case 'SLO_UNIT_SECONDS':
      return formatSecondsLatency(numeric);
    case 'SLO_UNIT_COUNT':
      return formatCount(numeric);
    default:
      return {
        value: String(dashesOrNum({ value: numeric, roundBy: 2 })),
      };
  }
};

const mapOverviewSloEntry = (
  entry: OrganizationOverviewSloMetricValue
): SloMetric | undefined => {
  if (!entry.metric) {
    return undefined;
  }

  const id = METRIC_ID_BY_API[entry.metric];
  if (!id) {
    return undefined;
  }

  const formatted = formatOverviewSloValue(entry.value, entry.unit);

  return {
    id,
    label: METRIC_LABEL_BY_ID[id] ?? id,
    value: formatted.value,
    unit: formatted.unit,
    status: mapSloStatus(entry.status),
  };
};

const orderOverviewSloMetrics = (metrics: SloMetric[]): SloMetric[] => {
  const byId = new Map(metrics.map((metric) => [metric.id, metric]));

  return OVERVIEW_SLO_METRIC_ORDER.flatMap((id) => {
    const metric = byId.get(id);
    return metric ? [metric] : [];
  });
};

export const mapOverviewSloToColumnData = (
  slo?: OrganizationOverviewSloMetricValue[]
): SloColumnData => {
  const metrics = orderOverviewSloMetrics(
    (slo ?? []).flatMap((entry) => {
      const mapped = mapOverviewSloEntry(entry);
      return mapped ? [mapped] : [];
    })
  );

  return {
    metrics,
    breachCount: metrics.filter((metric) => metric.status === 'breached')
      .length,
    riskCount: metrics.filter((metric) => metric.status === 'at_risk').length,
  };
};

export const deriveSloHeatmapFromOverview = (
  slo?: OrganizationOverviewSloMetricValue[]
): OverviewSloHeatmapData => {
  const { metrics, breachCount, riskCount } = mapOverviewSloToColumnData(slo);

  if (!metrics.length) {
    return { state: 'gathering', count: 0 };
  }

  if (breachCount > 0) {
    return { state: 'breach', count: breachCount };
  }

  if (riskCount > 0) {
    return { state: 'risk', count: riskCount };
  }

  return { state: 'ok', count: 0 };
};
